import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import RichTextEditor from "@/components/RichTextEditor";
import { SearchableSelect } from "@/components/SearchableSelect";
import { parseParis, parisNow } from "@/lib/parisTime";
import { 
  Globe, Search, MoreVertical, Pencil, Trash2, Eye, Copy, 
  ExternalLink, Calendar, User, Loader2, ChevronLeft, ChevronRight,
  RefreshCw, FileText, Filter, CheckSquare, Square, X, Image as ImageIcon, Upload, Link, Send
} from "lucide-react";

const decodeHtml = (s) => { const t = document.createElement("textarea"); t.innerHTML = s || ""; return t.value; };

const STATUS_TABS = [
  ["any", "Tous"],
  ["publish", "Publiés"],
  ["draft", "Brouillons"],
  ["pending", "En attente"],
  ["private", "Privés"],
  ["future", "Planifiés"],
];


export default function WPPostsPage() {
  const { token } = useAuth();
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSites, setLoadingSites] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("any");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  
  const [editPost, setEditPost] = useState(null);
  const [editData, setEditData] = useState({ title: "", content: "", excerpt: "", status: "", featured_image: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [deletePost, setDeletePost] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  const [siteAuthors, setSiteAuthors] = useState([]);
  const [siteCategories, setSiteCategories] = useState([]);
  
  // Bulk selection state
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Inline image editing state
  const [expandedPost, setExpandedPost] = useState(null);
  const [inlineImageUrl, setInlineImageUrl] = useState("");
  const [inlineUploading, setInlineUploading] = useState(false);
  const [inlineSaving, setInlineSaving] = useState(false);
  const [inlineData, setInlineData] = useState({});

  useEffect(() => {
    fetchSites();
  }, []);

  useEffect(() => {
    if (selectedSite) {
      fetchPosts();
      fetchSiteData();
      // Clear selection when changing site
      setSelectedPosts([]);
    }
  }, [selectedSite, currentPage, statusFilter]);

  const fetchSites = async () => {
    try {
      const response = await fetch(`${API}/sites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSites(data);
        if (data.length > 0) {
          setSelectedSite(data[0]);
        }
      }
    } catch (error) {
      console.error("Failed to fetch sites:", error);
    } finally {
      setLoadingSites(false);
    }
  };

  const fetchSiteData = async () => {
    if (!selectedSite) return;
    
    try {
      const [authorsRes, catsRes] = await Promise.all([
        fetch(`${API}/sites/${selectedSite.id}/authors`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/sites/${selectedSite.id}/categories`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      if (authorsRes.ok) setSiteAuthors(await authorsRes.json());
      if (catsRes.ok) setSiteCategories(await catsRes.json());
    } catch (error) {
      console.error("Failed to fetch site data:", error);
    }
  };

  const fetchPosts = async ({ silent = false } = {}) => {
    if (!selectedSite) return;
    fetchStatusCounts();
    
    if (!silent) setLoading(true);
    try {
      const response = await fetch(
        `${API}/sites/${selectedSite.id}/posts?per_page=20&page=${currentPage}&post_status=${statusFilter}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts || []);
        setTotalPages(data.total_pages || 1);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error("Failed to fetch posts:", error);
      toast.error("Erreur lors du chargement des articles");
    } finally {
      setLoading(false);
    }
  };

  const fetchStatusCounts = async () => {
    try {
      const res = await fetch(`${API}/sites/${selectedSite.id}/posts-counts`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setStatusCounts(await res.json());
    } catch (error) {
      console.error("Failed to fetch counts:", error);
    }
  };

  const handleEdit = (post) => {
    setEditPost(post);
    setEditData({
      title: post.title.replace(/<[^>]*>/g, ''),
      content: post.content,
      excerpt: post.excerpt.replace(/<[^>]*>/g, ''),
      status: post.status,
      author: post.author,
      categories: post.categories || [],
      featured_image: post.featured_image_url || ""
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Veuillez sélectionner une image");
      return;
    }

    setUploading(true);
    try {
      const imageName = editData.title?.trim() || "image-article";
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('name', imageName);

      const response = await fetch(`${API}/media/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        setEditData(prev => ({ ...prev, featured_image: data.url }));
        toast.success("Image téléchargée");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors du téléchargement");
      }
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    } finally {
      setUploading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editPost || !selectedSite) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API}/sites/${selectedSite.id}/posts/${editPost.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(editData)
      });
      
      if (response.ok) {
        toast.success("Article mis à jour");
        setEditPost(null);
        fetchPosts({ silent: true });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la mise à jour");
      }
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (force = false) => {
    if (!deletePost || !selectedSite) return;
    
    setDeleting(true);
    try {
      const response = await fetch(
        `${API}/sites/${selectedSite.id}/posts/${deletePost.id}?force=${force}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        toast.success(force ? "Article supprimé définitivement" : "Article mis à la corbeille");
        setDeletePost(null);
        fetchPosts({ silent: true });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleting(false);
    }
  };

  const handleDuplicate = async (post) => {
    if (!selectedSite) return;
    
    try {
      const response = await fetch(`${API}/sites/${selectedSite.id}/posts/${post.id}/duplicate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success("Article dupliqué");
        fetchPosts({ silent: true });
      } else {
        toast.error("Erreur lors de la duplication");
      }
    } catch (error) {
      toast.error("Erreur lors de la duplication");
    }
  };

  // Bulk selection handlers
  const toggleSelectPost = (postId) => {
    setSelectedPosts(prev => 
      prev.includes(postId) 
        ? prev.filter(id => id !== postId)
        : [...prev, postId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedPosts.length === filteredPosts.length) {
      setSelectedPosts([]);
    } else {
      setSelectedPosts(filteredPosts.map(p => p.id));
    }
  };

  const clearSelection = () => {
    setSelectedPosts([]);
  };

  const handleBulkDelete = async (force = false) => {
    if (!selectedSite || selectedPosts.length === 0) return;
    
    setBulkDeleting(true);
    try {
      const response = await fetch(
        `${API}/sites/${selectedSite.id}/posts/bulk-delete?force=${force}`,
        {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}` 
          },
          body: JSON.stringify({ post_ids: selectedPosts })
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`${result.deleted_count} article(s) ${force ? 'supprimé(s) définitivement' : 'mis à la corbeille'}`);
        setSelectedPosts([]);
        setBulkDeleteOpen(false);
        fetchPosts({ silent: true });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setBulkDeleting(false);
    }
  };

  // Toggle inline image panel for a post
  const toggleInlineEdit = (post) => {
    if (expandedPost === post.id) {
      setExpandedPost(null);
      setInlineImageUrl("");
      setInlineData({});
    } else {
      setExpandedPost(post.id);
      setInlineImageUrl(post.featured_image_url || "");
      setInlineData({
        status: post.status,
        author: post.author,
        categories: post.categories || [],
        date: post.date?.split('T')[0] || '',
        time: post.date?.split('T')[1]?.substring(0, 5) || ''
      });
    }
  };

  // Handle inline image upload
  const handleInlineImageUpload = async (e, post) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Veuillez sélectionner une image");
      return;
    }

    setInlineUploading(true);
    try {
      const imageName = post.title?.replace(/<[^>]*>/g, '') || "image-article";
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('name', imageName);

      const response = await fetch(`${API}/media/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        setInlineImageUrl(data.url);
        toast.success("Image téléchargée");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors du téléchargement");
      }
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    } finally {
      setInlineUploading(false);
    }
  };

  // Save inline changes to WordPress - accepts explicit data to avoid stale closure issues
  const saveInlineChanges = async (post, options = {}) => {
    if (!selectedSite) return;
    
    const { 
      status,           // 'publish' or 'future'
      imageUrl,         // featured image URL
      author,           // author ID
      categories,       // array of category IDs
      date,             // date string YYYY-MM-DD
      time,             // time string HH:MM
      useScheduling     // whether to send date (true for Planifier, false for Publier)
    } = options;
    
    setInlineSaving(true);
    try {
      // Build the update payload from explicit parameters
      const updateData = {};
      
      // Always set status when provided
      if (status) {
        updateData.status = status;
      }
      
      // Add image if provided and different from current
      if (imageUrl && imageUrl !== post.featured_image_url) {
        updateData.featured_image = imageUrl;
      }
      
      // Add author if provided and different
      if (author && author !== post.author) {
        updateData.author = author;
      }
      
      // Add categories if provided and different
      if (categories && JSON.stringify(categories) !== JSON.stringify(post.categories || [])) {
        updateData.categories = categories;
      }
      
      // Add date only if scheduling (Planifier button)
      if (useScheduling && date && time) {
        const dateTime = `${date}T${time}:00`;
        updateData.date = dateTime;
      }
      
      // If nothing changed, just close
      if (Object.keys(updateData).length === 0) {
        toast.info("Aucune modification détectée");
        setExpandedPost(null);
        setInlineImageUrl("");
        setInlineData({});
        return;
      }

      const response = await fetch(`${API}/sites/${selectedSite.id}/posts/${post.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });
      
      if (response.ok) {
        const result = await response.json();
        const wpStatus = result.post?.status;
        if (status === "future" && wpStatus !== "future") {
          toast.warning(`WordPress a ${wpStatus === "publish" ? "publié directement" : "enregistré"} l'article (date passée ?)`);
        } else if (wpStatus === "future") {
          toast.success(`Planifié sur WordPress pour le ${parseParis(result.post.date).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })} (heure de Paris)`);
        } else if (wpStatus === "publish") {
          toast.success("Publié sur WordPress !");
        } else {
          toast.success("Article mis à jour !");
        }
        setExpandedPost(null);
        setInlineImageUrl("");
        setInlineData({});
        fetchPosts({ silent: true });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la mise à jour");
      }
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setInlineSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      publish: "bg-[#20C997] hover:bg-[#20C997]",
      draft: "bg-gray-200 text-[#6B7280] hover:bg-gray-200",
      pending: "bg-amber-500 hover:bg-amber-500",
      private: "bg-[#1FB6FF] hover:bg-[#1FB6FF]",
      future: "bg-[#1E2A5A] hover:bg-[#1E2A5A]",
      trash: "bg-red-500 hover:bg-red-500"
    };
    const labels = {
      publish: "Publié",
      draft: "Brouillon",
      pending: "En attente",
      private: "Privé",
      future: "Planifié",
      trash: "Corbeille"
    };
    return <Badge className={`rounded-lg ${styles[status] || "bg-gray-500"}`}>{labels[status] || status}</Badge>;
  };

  const filteredPosts = posts.filter(post => 
    post.title.toLowerCase().includes(search.toLowerCase())
  );

  if (loadingSites) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#20C997]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="wp-posts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#1E2A5A]">Articles WordPress</h1>
          <p className="text-[#6B7280] mt-1">Gérez les articles de vos sites WordPress</p>
        </div>
      </div>

      {/* Site Selector & Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Select
          value={selectedSite?.id || ""}
          onValueChange={(id) => {
            const site = sites.find(s => s.id === id);
            setSelectedSite(site);
            setCurrentPage(1);
          }}
        >
          <SelectTrigger className="w-full sm:w-[250px] h-12 rounded-xl border-gray-200">
            <Globe className="w-4 h-4 mr-2 text-[#1E2A5A]" />
            <SelectValue placeholder="Sélectionner un site" />
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            {sites.map((site) => (
              <SelectItem key={site.id} value={site.id}>
                {site.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
          <Input
            placeholder="Rechercher..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-11 h-12 rounded-xl border-gray-200 focus:border-[#20C997] focus:ring-[#20C997]"
          />
        </div>

        <Button variant="outline" onClick={() => fetchPosts()} disabled={loading} className="h-12 rounded-xl border-gray-200 text-[#1E2A5A] hover:bg-[#F5F7FA]">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Status Tabs */}
      {selectedSite && (
        <div className="flex flex-wrap gap-2" data-testid="status-tabs">
          {STATUS_TABS.map(([value, label]) => (
            <button
              key={value}
              data-testid={`status-tab-${value}`}
              onClick={() => { setStatusFilter(value); setCurrentPage(1); setSelectedPosts([]); }}
              className={`flex items-center gap-2 px-4 h-10 rounded-full text-sm font-medium border transition-colors ${
                statusFilter === value
                  ? "bg-[#1E2A5A] text-white border-[#1E2A5A]"
                  : "bg-white text-[#1E2A5A] border-gray-200 hover:bg-[#F5F7FA]"
              }`}
            >
              {label}
              <span
                data-testid={`status-count-${value}`}
                className={`min-w-[1.5rem] px-1.5 rounded-full text-xs ${statusFilter === value ? "bg-[#20C997] text-white" : "bg-gray-100 text-[#6B7280]"}`}
              >
                {statusCounts[value] ?? "…"}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Stats */}
      {selectedSite && (
        <div className="flex items-center gap-4 text-sm text-[#6B7280]">
          <span>{total} article{total > 1 ? 's' : ''} sur <span className="font-medium text-[#1E2A5A]">{selectedSite.name}</span></span>
        </div>
      )}

      {/* Bulk Actions Bar */}
      {selectedPosts.length > 0 && (
        <div className="flex flex-wrap gap-3 items-center justify-between bg-[#1E2A5A] text-white p-4 rounded-2xl animate-fade-in">
          <div className="flex items-center gap-3">
            <CheckSquare className="w-5 h-5 text-[#20C997]" />
            <span className="font-medium">{selectedPosts.length} article{selectedPosts.length > 1 ? 's' : ''} sélectionné{selectedPosts.length > 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={clearSelection}
              className="text-white hover:bg-white/10 rounded-xl"
            >
              <X className="w-4 h-4 mr-2" />
              Annuler
            </Button>
            <Button 
              variant="destructive" 
              size="sm" 
              onClick={() => setBulkDeleteOpen(true)}
              className="bg-red-500 hover:bg-red-600 rounded-xl"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer ({selectedPosts.length})
            </Button>
          </div>
        </div>
      )}

      {/* Posts List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#20C997]" />
        </div>
      ) : filteredPosts.length > 0 ? (
        <div className="space-y-3">
          {/* Select All Header */}
          <div className="flex items-center gap-3 px-2 py-2 text-sm text-[#6B7280]">
            <Checkbox
              checked={selectedPosts.length === filteredPosts.length && filteredPosts.length > 0}
              onCheckedChange={toggleSelectAll}
              className="border-gray-300 data-[state=checked]:bg-[#20C997] data-[state=checked]:border-[#20C997]"
              data-testid="select-all-checkbox"
            />
            <span className="cursor-pointer" onClick={toggleSelectAll}>
              {selectedPosts.length === filteredPosts.length && filteredPosts.length > 0 
                ? "Tout désélectionner" 
                : "Tout sélectionner"}
            </span>
          </div>
          
          {filteredPosts.map((post) => {
            // Get current inline data for this post or use defaults
            const postInline = expandedPost === post.id ? inlineData : {
              status: post.status,
              author: post.author,
              categories: post.categories || [],
              date: post.date?.split('T')[0] || '',
              time: post.date?.split('T')[1]?.substring(0, 5) || ''
            };
            const postImageUrl = expandedPost === post.id ? inlineImageUrl : (post.featured_image_url || '');
            
            return (
            <Card key={post.id} className={`border-0 bg-white rounded-2xl soft-shadow transition-all duration-200 ${selectedPosts.includes(post.id) ? 'ring-2 ring-[#20C997]' : ''}`}>
              <CardContent className="p-4">
                {/* Header row: checkbox, badge, title, copy button, menu */}
                <div className="flex items-start gap-3 mb-3">
                  <Checkbox
                    checked={selectedPosts.includes(post.id)}
                    onCheckedChange={() => toggleSelectPost(post.id)}
                    className="mt-1 border-gray-300 data-[state=checked]:bg-[#20C997] data-[state=checked]:border-[#20C997]"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {getStatusBadge(post.status)}
                      <span className="text-xs text-[#6B7280]">ID: {post.id}</span>
                    </div>
                    {/* Full title - no truncation */}
                    <div className="flex items-start gap-2">
                      <h3 
                        className="font-semibold text-base text-[#1E2A5A] flex-1"
                        dangerouslySetInnerHTML={{ __html: post.title }}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 px-2 text-[#6B7280] hover:text-[#20C997] hover:bg-[#20C997]/10 hover:border-[#20C997] border-gray-200 rounded-lg flex-shrink-0"
                        onClick={() => {
                          // Decode HTML entities properly
                          const tempDiv = document.createElement('div');
                          tempDiv.innerHTML = post.title || '';
                          const plainTitle = tempDiv.textContent || tempDiv.innerText || '';
                          navigator.clipboard.writeText(plainTitle);
                          toast.success("Titre copié !");
                        }}
                        title="Copier le titre"
                        data-testid={`copy-title-btn-${post.id}`}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="text-[#6B7280] hover:text-[#1E2A5A] h-8 w-8">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="rounded-xl">
                      <DropdownMenuItem onClick={() => window.open(post.link, '_blank')} className="cursor-pointer">
                        <Eye className="w-4 h-4 mr-2" />
                        Voir sur le site
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleEdit(post)} className="cursor-pointer">
                        <Pencil className="w-4 h-4 mr-2" />
                        Modifier contenu
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDuplicate(post)} className="cursor-pointer">
                        <Copy className="w-4 h-4 mr-2" />
                        Dupliquer
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setDeletePost(post)} className="text-red-500 cursor-pointer">
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Article content preview - scrollable */}
                {post.content && (
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-[#6B7280]">Aperçu du contenu</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs text-[#6B7280] hover:text-[#20C997]"
                        onClick={() => handleEdit(post)}
                        data-testid={`open-editor-btn-${post.id}`}
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        Ouvrir
                      </Button>
                    </div>
                    <div 
                      className="max-h-[100px] overflow-y-auto bg-[#F5F7FA] rounded-xl p-3 text-sm text-[#6B7280] scrollbar-thin"
                    >
                      {/* Strip HTML and show plain text only for security and performance */}
                      {(() => {
                        const tempDiv = document.createElement('div');
                        tempDiv.innerHTML = post.content;
                        return tempDiv.textContent || tempDiv.innerText || '';
                      })()}
                    </div>
                  </div>
                )}

                {/* Quick edit form - always visible */}
                <div className="space-y-3 pt-3 border-t border-gray-100">
                  {/* Row 1: Category & Author dropdowns */}
                  <div className="grid grid-cols-2 gap-2">
                    {/* Category dropdown */}
                    <SearchableSelect
                      testId={`category-select-${post.id}`}
                      placeholder="Catégorie"
                      searchPlaceholder="Rechercher une catégorie..."
                      className="h-9 text-xs"
                      options={siteCategories.map((c) => ({ value: c.id.toString(), label: decodeHtml(c.name) }))}
                      value={(postInline.categories?.[0] || post.categories?.[0])?.toString() || ""}
                      onChange={(v) => {
                        if (expandedPost !== post.id) {
                          setExpandedPost(post.id);
                          setInlineData({
                            status: post.status,
                            author: post.author,
                            categories: [parseInt(v)],
                            date: post.date?.split('T')[0] || '',
                            time: post.date?.split('T')[1]?.substring(0, 5) || ''
                          });
                          setInlineImageUrl(post.featured_image_url || '');
                        } else {
                          setInlineData(prev => ({ ...prev, categories: [parseInt(v)] }));
                        }
                      }}
                    />

                    {/* Author dropdown */}
                    <SearchableSelect
                      testId={`author-select-${post.id}`}
                      placeholder="Auteur"
                      searchPlaceholder="Rechercher un auteur..."
                      className="h-9 text-xs"
                      options={siteAuthors.map((a) => ({ value: a.id.toString(), label: a.name }))}
                      value={(postInline.author || post.author)?.toString() || ""}
                      onChange={(v) => {
                        if (expandedPost !== post.id) {
                          setExpandedPost(post.id);
                          setInlineData({
                            status: post.status,
                            author: parseInt(v),
                            categories: post.categories || [],
                            date: post.date?.split('T')[0] || '',
                            time: post.date?.split('T')[1]?.substring(0, 5) || ''
                          });
                          setInlineImageUrl(post.featured_image_url || '');
                        } else {
                          setInlineData(prev => ({ ...prev, author: parseInt(v) }));
                        }
                      }}
                    />
                  </div>

                  {/* Row 2: Image URL input */}
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Lien de l'image..."
                        value={expandedPost === post.id ? inlineImageUrl : (post.featured_image_url || '')}
                        onChange={(e) => {
                          if (expandedPost !== post.id) {
                            setExpandedPost(post.id);
                            setInlineData({
                              status: post.status,
                              author: post.author,
                              categories: post.categories || [],
                              date: post.date?.split('T')[0] || '',
                              time: post.date?.split('T')[1]?.substring(0, 5) || ''
                            });
                          }
                          setInlineImageUrl(e.target.value);
                        }}
                        className="flex-1 h-9 rounded-xl border-gray-200 text-xs"
                      />
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            if (expandedPost !== post.id) {
                              setExpandedPost(post.id);
                              setInlineData({
                                status: post.status,
                                author: post.author,
                                categories: post.categories || [],
                                date: post.date?.split('T')[0] || '',
                                time: post.date?.split('T')[1]?.substring(0, 5) || ''
                              });
                            }
                            handleInlineImageUpload(e, post);
                          }}
                          className="hidden"
                          disabled={inlineUploading}
                        />
                        <span className="inline-flex items-center justify-center w-9 h-9 bg-[#F5F7FA] hover:bg-gray-200 rounded-xl transition-colors">
                          {inlineUploading && expandedPost === post.id ? (
                            <Loader2 className="w-4 h-4 animate-spin text-[#6B7280]" />
                          ) : (
                            <Upload className="w-4 h-4 text-[#6B7280]" />
                          )}
                        </span>
                      </label>
                    </div>
                    
                    {/* Image Preview - Always show a square */}
                    <div data-testid={`image-preview-${post.id}`} className="relative rounded-xl overflow-hidden border border-gray-200 w-full max-w-xs sm:w-64 aspect-video bg-[#F5F7FA]">
                      {(expandedPost === post.id ? inlineImageUrl : post.featured_image_url) ? (
                        <>
                          <img 
                            src={expandedPost === post.id ? inlineImageUrl : post.featured_image_url} 
                            alt="Aperçu" 
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                            }}
                            onLoad={(e) => {
                              e.target.style.display = 'block';
                              if (e.target.nextSibling) e.target.nextSibling.style.display = 'none';
                            }}
                          />
                          <div className="hidden w-full h-full bg-red-50 items-center justify-center text-red-500 text-xs">
                            Image non valide
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-[#6B7280]">
                          <ImageIcon className="w-6 h-6 mb-1 opacity-50" />
                          <span className="text-[10px]">Pas d'image</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Row 3: Date, Time picker */}
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="date"
                      value={expandedPost === post.id ? (inlineData.date || '') : (post.date?.split('T')[0] || '')}
                      onChange={(e) => {
                        if (expandedPost !== post.id) {
                          setExpandedPost(post.id);
                          setInlineData({
                            status: post.status,
                            author: post.author,
                            categories: post.categories || [],
                            date: e.target.value,
                            time: post.date?.split('T')[1]?.substring(0, 5) || ''
                          });
                          setInlineImageUrl(post.featured_image_url || '');
                        } else {
                          setInlineData(prev => ({ ...prev, date: e.target.value }));
                        }
                      }}
                      className="h-9 rounded-xl border-gray-200 text-xs"
                    />
                    <Input
                      type="time"
                      value={expandedPost === post.id ? (inlineData.time || '') : (post.date?.split('T')[1]?.substring(0, 5) || '')}
                      onChange={(e) => {
                        if (expandedPost !== post.id) {
                          setExpandedPost(post.id);
                          setInlineData({
                            status: post.status,
                            author: post.author,
                            categories: post.categories || [],
                            date: post.date?.split('T')[0] || '',
                            time: e.target.value
                          });
                          setInlineImageUrl(post.featured_image_url || '');
                        } else {
                          setInlineData(prev => ({ ...prev, time: e.target.value }));
                        }
                      }}
                      className="h-9 rounded-xl border-gray-200 text-xs"
                    />
                  </div>
                  <p className="text-[10px] text-[#6B7280] -mt-1">Date et heure de Paris</p>

                  {/* Row 4: Action buttons - Planifier & Publier */}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 h-9 rounded-xl text-xs border-[#1E2A5A] text-[#1E2A5A] hover:bg-[#1E2A5A] hover:text-white"
                      disabled={inlineSaving && expandedPost === post.id}
                      data-testid={`schedule-btn-${post.id}`}
                      onClick={() => {
                        // Planifier = use selected date/time with status 'future'
                        // Get current values from inline state if this post is expanded, otherwise from post
                        const currentImageUrl = expandedPost === post.id ? inlineImageUrl : (post.featured_image_url || '');
                        const currentAuthor = expandedPost === post.id ? (inlineData.author || post.author) : post.author;
                        const currentCategories = expandedPost === post.id ? (inlineData.categories || post.categories || []) : (post.categories || []);
                        const dateVal = expandedPost === post.id ? (inlineData.date || post.date?.split('T')[0]) : post.date?.split('T')[0];
                        const timeVal = expandedPost === post.id ? (inlineData.time || post.date?.split('T')[1]?.substring(0, 5)) : post.date?.split('T')[1]?.substring(0, 5);
                        
                        if (!dateVal || !timeVal) {
                          toast.error("Veuillez sélectionner une date et une heure");
                          return;
                        }
                        if (parseParis(`${dateVal}T${timeVal}:00`) <= parisNow()) {
                          toast.error("La date doit être dans le futur (heure de Paris)");
                          return;
                        }
                        
                        // Call save directly with explicit data - no setTimeout, no stale closures
                        saveInlineChanges(post, {
                          status: 'future',
                          imageUrl: currentImageUrl,
                          author: currentAuthor,
                          categories: currentCategories,
                          date: dateVal,
                          time: timeVal,
                          useScheduling: true
                        });
                      }}
                    >
                      {inlineSaving && expandedPost === post.id ? (
                        <Loader2 className="w-3 h-3 animate-spin mr-1" />
                      ) : (
                        <Calendar className="w-3 h-3 mr-1" />
                      )}
                      Planifier
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1 h-9 rounded-xl text-xs bg-[#20C997] hover:bg-[#1E2A5A]"
                      disabled={inlineSaving && expandedPost === post.id}
                      data-testid={`publish-btn-${post.id}`}
                      onClick={() => {
                        // Publier = immediate publish, ignore date/time
                        // Get current values from inline state if this post is expanded, otherwise from post
                        const currentImageUrl = expandedPost === post.id ? inlineImageUrl : (post.featured_image_url || '');
                        const currentAuthor = expandedPost === post.id ? (inlineData.author || post.author) : post.author;
                        const currentCategories = expandedPost === post.id ? (inlineData.categories || post.categories || []) : (post.categories || []);
                        
                        // Call save directly with explicit data - no setTimeout, no stale closures
                        saveInlineChanges(post, {
                          status: 'publish',
                          imageUrl: currentImageUrl,
                          author: currentAuthor,
                          categories: currentCategories,
                          date: '',
                          time: '',
                          useScheduling: false
                        });
                      }}
                    >
                      {inlineSaving && expandedPost === post.id ? (
                        <Loader2 className="w-3 h-3 animate-spin mr-1" />
                      ) : (
                        <Send className="w-3 h-3 mr-1" />
                      )}
                      Publier
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
          })}
        </div>
      ) : (
        <Card className="border-0 bg-white rounded-2xl soft-shadow">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="w-20 h-20 bg-[#F5F7FA] rounded-full flex items-center justify-center mb-4">
              <FileText className="w-10 h-10 text-[#6B7280]/50" />
            </div>
            <p className="text-[#6B7280]">
              {search ? "Aucun article trouvé" : "Aucun article sur ce site"}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1 || loading}
            className="rounded-xl border-gray-200 text-[#1E2A5A] hover:bg-[#F5F7FA]"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Précédent
          </Button>
          <span className="text-sm text-[#6B7280]">
            Page <span className="font-medium text-[#1E2A5A]">{currentPage}</span> sur <span className="font-medium text-[#1E2A5A]">{totalPages}</span>
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages || loading}
            className="rounded-xl border-gray-200 text-[#1E2A5A] hover:bg-[#F5F7FA]"
          >
            Suivant
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editPost} onOpenChange={() => setEditPost(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#1E2A5A]">Modifier l'article</DialogTitle>
            <DialogDescription className="text-[#6B7280]">
              Modifiez l'article directement sur WordPress
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-[#1E2A5A]">Titre</Label>
              <div className="flex gap-2">
                <Input
                  value={editData.title}
                  onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                  className="flex-1 rounded-xl border-gray-200 focus:border-[#20C997] focus:ring-[#20C997]"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="rounded-xl border-gray-200 hover:bg-[#20C997]/10 hover:text-[#20C997]"
                  onClick={() => {
                    navigator.clipboard.writeText(editData.title);
                    toast.success("Titre copié !");
                  }}
                  title="Copier le titre"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-[#1E2A5A]">Contenu</Label>
              <RichTextEditor
                content={editData.content}
                onChange={(content) => setEditData({ ...editData, content })}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#1E2A5A]">Statut</Label>
                <Select
                  value={editData.status}
                  onValueChange={(v) => setEditData({ ...editData, status: v })}
                >
                  <SelectTrigger className="rounded-xl border-gray-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    <SelectItem value="publish">Publié</SelectItem>
                    <SelectItem value="draft">Brouillon</SelectItem>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="private">Privé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#1E2A5A]">Auteur</Label>
                <Select
                  value={editData.author?.toString() || ""}
                  onValueChange={(v) => setEditData({ ...editData, author: parseInt(v) })}
                >
                  <SelectTrigger className="rounded-xl border-gray-200">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {siteAuthors.map((author) => (
                      <SelectItem key={author.id} value={author.id.toString()}>
                        {author.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Catégories */}
            {siteCategories.length > 0 && (
              <div className="space-y-2">
                <Label className="text-[#1E2A5A]">Catégories</Label>
                <div className="max-h-[150px] overflow-y-auto border border-gray-200 rounded-xl p-3 space-y-2 scrollbar-thin">
                  {siteCategories.map((cat) => (
                    <div 
                      key={cat.id}
                      className={`flex items-center space-x-2 p-2 rounded-lg cursor-pointer transition-colors ${
                        editData.categories?.includes(cat.id)
                          ? 'bg-[#20C997]/10'
                          : 'hover:bg-[#F5F7FA]'
                      }`}
                      onClick={() => {
                        const cats = editData.categories || [];
                        if (cats.includes(cat.id)) {
                          setEditData({ ...editData, categories: cats.filter(c => c !== cat.id) });
                        } else {
                          setEditData({ ...editData, categories: [...cats, cat.id] });
                        }
                      }}
                    >
                      <Checkbox
                        checked={editData.categories?.includes(cat.id)}
                        className="border-gray-300 data-[state=checked]:bg-[#20C997] data-[state=checked]:border-[#20C997]"
                      />
                      <span className="text-sm text-[#1E2A5A]">{cat.name}</span>
                      <Badge className="ml-auto text-xs rounded-lg bg-[#F5F7FA] text-[#6B7280]">{cat.count}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Image à la une */}
            <div className="space-y-3 p-4 border border-gray-200 rounded-xl bg-[#F5F7FA]/50">
              <Label className="text-[#1E2A5A] flex items-center gap-2">
                <ImageIcon className="w-4 h-4" />
                Image à la une
              </Label>
              
              {/* URL Input */}
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    placeholder="https://... coller le lien de l'image"
                    value={editData.featured_image || ""}
                    onChange={(e) => setEditData({ ...editData, featured_image: e.target.value })}
                    className="flex-1 rounded-xl border-gray-200 focus:border-[#20C997] focus:ring-[#20C997]"
                  />
                </div>
              </div>

              {/* Separator */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-300"></div>
                <span className="text-xs text-[#6B7280]">ou</span>
                <div className="flex-1 h-px bg-gray-300"></div>
              </div>

              {/* Upload */}
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-3 text-center hover:border-[#20C997]/50 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="wp-image-upload"
                  disabled={uploading}
                />
                <label htmlFor="wp-image-upload" className="cursor-pointer flex items-center justify-center gap-2">
                  {uploading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-[#20C997]" />
                  ) : (
                    <Upload className="w-5 h-5 text-[#6B7280]" />
                  )}
                  <span className="text-sm text-[#6B7280]">
                    {uploading ? "Téléchargement..." : "Télécharger une image"}
                  </span>
                </label>
              </div>

              {/* Image Preview */}
              {editData.featured_image && (
                <div className="relative rounded-xl overflow-hidden border border-gray-200">
                  <img 
                    src={editData.featured_image} 
                    alt="Preview" 
                    className="w-full h-32 object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                    onLoad={(e) => {
                      e.target.style.display = 'block';
                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'none';
                    }}
                  />
                  <div className="hidden w-full h-32 bg-red-50 items-center justify-center text-red-500 text-sm">
                    Image non valide ou inaccessible
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2 h-6 w-6 rounded-lg"
                    onClick={() => setEditData({ ...editData, featured_image: "" })}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPost(null)} className="rounded-xl">
              Annuler
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving} className="bg-[#1E2A5A] hover:bg-[#20C997] rounded-xl transition-colors">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deletePost} onOpenChange={() => setDeletePost(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#1E2A5A]">Supprimer cet article ?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#6B7280]">
              Choisissez de mettre l'article à la corbeille ou de le supprimer définitivement.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="rounded-xl">Annuler</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => handleDelete(false)}
              disabled={deleting}
              className="rounded-xl"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Mettre à la corbeille
            </Button>
            <AlertDialogAction
              onClick={() => handleDelete(true)}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600 rounded-xl"
            >
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Dialog */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#1E2A5A]">
              Supprimer {selectedPosts.length} article{selectedPosts.length > 1 ? 's' : ''} ?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6B7280]">
              Choisissez de mettre les articles à la corbeille ou de les supprimer définitivement.
              Cette action ne peut pas être annulée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="rounded-xl" disabled={bulkDeleting}>Annuler</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => handleBulkDelete(false)}
              disabled={bulkDeleting}
              className="rounded-xl"
            >
              {bulkDeleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Mettre à la corbeille
            </Button>
            <AlertDialogAction
              onClick={() => handleBulkDelete(true)}
              disabled={bulkDeleting}
              className="bg-red-500 hover:bg-red-600 rounded-xl"
            >
              {bulkDeleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
